import { Dropdown, Form, Input } from 'semantic-ui-react';

import { JsonPayloadEditor } from './json-payload-editor';
import {
  defaultModeFor,
  METHOD_OPTIONS,
  MODE_OPTIONS,
} from './madrobot-modal.utils';
import type { FailureMode, Method, Rule } from './madrobot.models';
import { INVALID_JSON_PAYLOAD } from './madrobot.schema';
import s from './madrobot-modal.scss';

interface ModeParamsProps {
  mode: FailureMode;
  onChange: (mode: FailureMode) => void;
}

const ModeParams = ({ mode, onChange }: ModeParamsProps) => {
  switch (mode.kind) {
    case 'httpError':
      return (
        <>
          <Form.Field>
            <label htmlFor="madrobot-mode-status">Status code</label>
            <Input
              id="madrobot-mode-status"
              max={599}
              min={100}
              onChange={(_, { value }) => {
                const n = Number(value);
                if (!Number.isNaN(n)) onChange({ ...mode, status: n });
              }}
              type="number"
              value={mode.status}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="madrobot-mode-body">
              Body (JSON, optional — replaces the default error envelope)
            </label>
            <JsonPayloadEditor
              ariaLabel="Response body JSON editor"
              onChange={body => onChange({ ...mode, body })}
              value={mode.body}
            />
          </Form.Field>
        </>
      );
    case 'latency':
      return (
        <Form.Field>
          <label htmlFor="madrobot-mode-delay">Delay (ms)</label>
          <Input
            id="madrobot-mode-delay"
            min={0}
            onChange={(_, { value }) => {
              const n = Number(value);
              if (!Number.isNaN(n)) onChange({ ...mode, delayMs: n });
            }}
            type="number"
            value={mode.delayMs}
          />
        </Form.Field>
      );
    case 'networkDrop':
      return null;
    case 'invalidJson':
      return (
        <Form.Field>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/label-has-for -- labels a static preview, not a form control */}
          <label>Raw response body (returned verbatim, not editable)</label>
          <pre className={s.invalidJsonPreview}>{INVALID_JSON_PAYLOAD}</pre>
        </Form.Field>
      );
    case 'wrongShape':
      return (
        <>
          <Form.Field>
            <label htmlFor="madrobot-mode-wrong-shape-status">
              Status code
            </label>
            <Input
              id="madrobot-mode-wrong-shape-status"
              max={599}
              min={100}
              onChange={(_, { value }) => {
                if (value === '') {
                  const { status: _omit, ...rest } = mode;
                  onChange(rest);
                  return;
                }
                const n = Number(value);
                if (!Number.isNaN(n)) onChange({ ...mode, status: n });
              }}
              placeholder="200"
              type="number"
              value={mode.status ?? ''}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="madrobot-mode-wrong-shape">
              Response body (JSON, returned verbatim)
            </label>
            <JsonPayloadEditor
              ariaLabel="Response body JSON editor"
              onChange={payload => {
                if (payload === undefined) return;
                onChange({ ...mode, payload });
              }}
              value={mode.payload}
            />
          </Form.Field>
        </>
      );
    default:
      return null;
  }
};

interface RuleEditorProps {
  draft: Rule;
  onChange: (rule: Rule) => void;
}

export const RuleEditor = ({ draft, onChange }: RuleEditorProps) => {
  const set = <K extends keyof Rule>(key: K, value: Rule[K]) =>
    onChange({ ...draft, [key]: value });

  const setMode = (mode: FailureMode) => onChange({ ...draft, mode });

  return (
    <Form>
      <Form.Field>
        <label htmlFor="madrobot-rule-name">Name</label>
        <Input
          id="madrobot-rule-name"
          onChange={(_, { value }) => set('name', value)}
          value={draft.name}
        />
      </Form.Field>
      <Form.Group widths="equal">
        <Form.Field>
          <label htmlFor="madrobot-rule-method">Method</label>
          <Dropdown
            id="madrobot-rule-method"
            onChange={(_, { value }) => set('method', value as Method)}
            options={METHOD_OPTIONS}
            selection
            value={draft.method}
          />
        </Form.Field>
        <Form.Field>
          <label htmlFor="madrobot-rule-path">Path pattern</label>
          <Input
            className={s.monoField}
            id="madrobot-rule-path"
            onChange={(_, { value }) => set('pathPattern', value)}
            placeholder="/applications{/*rest}"
            value={draft.pathPattern}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field>
          <label htmlFor="madrobot-rule-mode">Mode</label>
          <Dropdown
            id="madrobot-rule-mode"
            onChange={(_, { value }) =>
              setMode(defaultModeFor(value as FailureMode['kind']))
            }
            options={MODE_OPTIONS}
            selection
            value={draft.mode.kind}
          />
        </Form.Field>
        <Form.Field>
          <label htmlFor="madrobot-rule-probability">Probability (0–1)</label>
          <Input
            id="madrobot-rule-probability"
            max={1}
            min={0}
            onChange={(_, { value }) => {
              const n = Number(value);
              if (!Number.isNaN(n)) {
                set('probability', Math.min(1, Math.max(0, n)));
              }
            }}
            step={0.05}
            type="number"
            value={draft.probability}
          />
        </Form.Field>
      </Form.Group>

      <ModeParams mode={draft.mode} onChange={setMode} />
    </Form>
  );
};
